import React, { useEffect, useState, useContext } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import CorrectionNew from './CorrectionNew';
import DuplicateFieldsDialog from '../../common/DuplicateFieldsDialog';
import ErrorMsg from '../../common/ErrorMsg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FieldActions from '../../common/FieldActions';
import FieldList, { IFieldReview } from './FieldList';
import errorResponses from '../../../errorResponses';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { REFETCH_AFTER_CORRECTION_MS } from '../../../constants';
import { featureFlags } from '../../../utils/featureFlags';
import { useExtractionCorrected } from '../../hooks/useExtractionCorrected';
import { useQueryClient } from '@tanstack/react-query';
import { IExtractionResults } from '../../../types/my-types';
import { useReviewFields } from '../../hooks/useReviewFields';

import MarkReviewFormStyles from '../../mark/form/MarkReviewForm.module.scss';

export interface ReviewFormProps {
  ebId: string;
  extractionId: string;
  readOnly?: boolean;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ extractionId, ebId, readOnly = false }) => {
  const [isAddFieldActive, setAddFieldActive] = useState(false);
  const [newFieldHeight, setNewFieldHeight] = useState(0);
  const cache = useQueryClient();
  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const newFieldRef = React.useRef<HTMLDivElement>(null);
  const { correctionsInARowCount, tokens } = useContext(MarkStateContext);

  const { data: extraction, isLoading, isError } = useExtractionCorrected(extractionId);
  const fields: IFieldReview[] = useReviewFields(extraction?.results);

  // Only invalidate extraction query after REFETCH_AFTER_CORRECTION_MS from the last correction
  useEffect(() => {
    if (correctionsInARowCount === 0 || tokens?.length > 0) {
      return;
    }

    const timeout = setTimeout(() => {
      dispatch({ type: 'UPDATE_CORRECTIONS_COUNT', payload: 0 });
      cache.invalidateQueries({
        queryKey: ['extraction', extractionId],
      });
    }, REFETCH_AFTER_CORRECTION_MS);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctionsInARowCount, cache, tokens, dispatch]);

  // Dinamicly set the marginTop of the fieldList container based on the newField height
  useEffect(() => {
    if (isAddFieldActive && newFieldRef.current) {
      setNewFieldHeight(newFieldRef.current.offsetHeight);
    } else {
      setNewFieldHeight(0);
    }
  }, [isAddFieldActive]);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (isError) {
    return <ErrorMsg error={errorResponses.failedToLoadExtraction} />;
  }

  return (
    <>
      <div id="review-form" className={MarkReviewFormStyles.formBaseStyles}>
        {readOnly ? null : featureFlags.duplicateFields ? (
          <>
            <FieldActions
              isAddFieldActive={isAddFieldActive}
              setAddFieldActive={setAddFieldActive}
            />

            {/* 'display' CSS property required for Cypress testing; element must be in DOM */}
            <div
              ref={newFieldRef}
              className={`${MarkReviewFormStyles.formBaseStyles} ${MarkReviewFormStyles.newFieldContainer}`}
              style={{ display: isAddFieldActive ? 'block' : 'none' }}
            >
              <CorrectionNew ebId={ebId} extractionId={extractionId} fields={fields} />
            </div>
          </>
        ) : (
          <Accordion>
            <AccordionSummary
              data-cy="review-form-panel"
              expandIcon={<ExpandMoreIcon />}
              id="review-form-panel"
            >
              <Typography style={{ paddingRight: 15 }}>New Field</Typography>
              <AddCircleOutline />
            </AccordionSummary>
            <AccordionDetails>
              <div className={MarkReviewFormStyles.formBaseStyles}>
                <CorrectionNew ebId={ebId} extractionId={extractionId} fields={fields} />
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        <div
          data-cy="field-list-container"
          className={MarkReviewFormStyles.fieldListContainer}
          style={{
            height: `calc(100% - 63px
              ${newFieldHeight !== 0 ? ` - ${newFieldHeight}px` : ''})`,
          }}
        >
          {extraction ? (
            <FieldList ebId={ebId} extraction={extraction} fields={fields} readOnly={readOnly} />
          ) : null}
        </div>
      </div>

      <DuplicateFieldsDialog
        extractionId={extractionId}
        fieldLabels={fields.map((field) => field.label)}
        isReview={true}
      />
    </>
  );
};

export default ReviewForm;
